import { DataGridRef, Spinner, SortV2, SearchV2, FilterV2, ButtonV2, Modal } from '@cimplico/components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PageTitle from '../components/PageTitle';
import SecondaryPageActions from '../components/secondary-page-actions/SecondaryPageActions';
import OtherSchedulesTable from '../features/other-schedules/OtherSchedulesTable';
import OtherSchedulesTabs from '../features/other-schedules/OtherSchedulesTabs';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';
import { useUsers } from '../queries/use-users';
import GrowHeightContainer from '../components/GrowHeightContainer';
import { RootState } from '../store';
import { InternalCommentStatusEnum } from '../types/internal-comment';
import { ClientQueryStatusEnum } from '../types/client-query';
import WorksheetExcludeConfirm from '../features/worksheet/WorksheetExcludeConfirm';
import WorksheetIncludeConfirm from '../features/worksheet/WorksheetIncludeConfirm';
import EditDetailsForm from '../features/worksheet-v2/top-section/EditDetailsForm';
import { GenericWorksheet, Worksheet, WorksheetStatusEnum } from '../types/worksheet';
import AddAssigneesDialog from '../features/trial-balance/AddAssigneesDialog';
import RemoveAssigneesDialog from '../features/trial-balance/RemoveAssigneesDialog';
import DocumentBrowser from '../components/document-browser/DocumentBrowser';
import WorksheetReviewConfirm from '../features/worksheet/WorksheetReviewConfirm';
import BulkActionsErrorAlert from '../features/trial-balance/BulkActionErrors';
import { WorkpaperRoleEnum } from '../types/workpaper';
import NavBreadcrumbs from '../components/nav-breadcrumbs/NavBreadcrumbs';
import { isNullOrUndefined } from '../features/new-workpaper/utils';
import LinkPriorWorksheetsContainer from '../features/carry-forward/LinkPriorWorksheetsContainer';
import CimplicoStaffGuard from '../components/cimplico-staff-guard/CimplicoStaffGuard';
import BulkCarryForwardResourcesConfirm from '../features/carry-forward/BulkCarryForwardResourcesConfirm';

const sortOptions = [
    { label: 'Name', value: 'name' },
    { label: 'Outstanding Queries', value: 'outstandingClientQueries' },
    { label: 'Outstanding Comments', value: 'outstandingInternalComments' },
    { label: 'Outstanding Checklist Items', value: 'outstandingChecklistItems' },
];

function filterCanReview(worksheets: Worksheet[]): Worksheet[] {
    return worksheets.filter((ws) => ws.status === WorksheetStatusEnum.AWAITING_REVIEW);
}
/**
 * Filters the worksheets to only those that are able to be linked to a prior worksheet
 * @param worksheets the worksheets to filter
 * @param priorWorkpaperId if undefined, no worksheets are valid
 * @returns the filtered worksheets
 */
function filterCanLinkToPrior(worksheets: GenericWorksheet[], priorWorkpaperId?: string): GenericWorksheet[] {
    if (isNullOrUndefined(priorWorkpaperId)) return [];
    return worksheets.filter((ws) => isNullOrUndefined(ws.priorWorksheetId));
}

/**
 * Filters the worksheets to only those that are already linked to a prior worksheet
 * @param worksheets the worksheets to filter
 * @param priorWorkpaperId if undefined, no worksheets are valid
 * @returns worksheets that have a priorWorksheetId.
 */
function filterCanCarryForward(worksheets: GenericWorksheet[], priorWorkpaperId?: string): GenericWorksheet[] {
    if (isNullOrUndefined(priorWorkpaperId)) return [];
    return worksheets.filter((ws) => !isNullOrUndefined(ws.priorWorksheetId));
}

function OtherSchedulesPage(): JSX.Element {
    const [statusIndex, setStatusIndex] = useState(0);
    const [sort, setSort] = useState<string>('');
    const [query, setQuery] = useSearchParams();
    const [page, setPage] = useState(1);

    const tableRef = useRef<DataGridRef | null>(null);
    const { id: workpaperId, readOnly, role, priorWorkpaperId } = useWorkpaperContext();
    const userCanReview = [WorkpaperRoleEnum.REVIEWER, WorkpaperRoleEnum.APPROVER].includes(role);

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [currentSchedules, setCurrentSchedules] = useState<GenericWorksheet[]>([]);
    const selectedSchedules = useMemo(() => currentSchedules.filter((ws) => selectedIds.includes(ws.id)), [currentSchedules, selectedIds]);
    const [bulkActionErrors, setBulkActionErrors] = useState<string[] | undefined>();
    const [action, setAction] = useState<
        | 'EXCLUDE'
        | 'INCLUDE'
        | 'ADD_ASSIGNEES'
        | 'REMOVE_ASSIGNEES'
        | 'EDIT_DETAILS'
        | 'REVIEW'
        | 'LINK_TO_PRIOR'
        | 'CARRY_FORWARD'
        | undefined
    >();
    const [search, setSearch] = useState<string>('');
    const [filter, setFilter] = useState<string>('');
    const [uploading, setUploading] = useState(false);

    const usersQuery = useUsers({ page: 1, search: '', sort: '', filter: '' });

    const navigate = useNavigate();

    const canExclude = useMemo(
        () =>
            selectedSchedules.filter(
                (os) =>
                    os.status !== WorksheetStatusEnum.EXCLUDED &&
                    os.internalComments.every((ic) => ic.status === InternalCommentStatusEnum.RESOLVED) &&
                    os.clientQueries.every((cq) => cq.status === ClientQueryStatusEnum.RESOLVED)
            ),
        [selectedSchedules]
    );
    const canInclude = useMemo(() => selectedSchedules.filter((os) => os.status === WorksheetStatusEnum.EXCLUDED), [selectedSchedules]);

    const canLinkToPrior = useMemo(() => filterCanLinkToPrior(selectedSchedules, priorWorkpaperId), [selectedSchedules, priorWorkpaperId]);
    const canCarryForward = useMemo(
        () => filterCanCarryForward(selectedSchedules, priorWorkpaperId),
        [selectedSchedules, priorWorkpaperId]
    );

    const savedFilters = useSelector((state: RootState) => state.workpaperFilters.value[workpaperId]?.otherSchedules);

    const STATUS_FILTERS = ['ALL', ...Object.values(WorksheetStatusEnum)];

    useEffect(() => {
        if (savedFilters?.filterQuery || savedFilters?.sortQuery) {
            setQuery((s) => {
                if (s.get('tab')) {
                    const tab = s.get('tab');
                    return `tab=${tab}&${savedFilters.filterQuery || ''}&${savedFilters.sortQuery || ''}`;
                }
                return `${savedFilters.filterQuery || ''}&${savedFilters.sortQuery || ''}`;
            });
        }
    }, []);

    function handleStatusTabChange(idx: number): void {
        setStatusIndex(idx);
        setPage(1);
        tableRef.current?.resetPagination();
    }

    /**
     * Formats the errors from a bulk action into a more readable format.
     *
     * @param errors - The errors from the bulk action, keyed by worksheet ID.
     * @returns The formatted errors, with the worksheet name included.
     */
    function formatErrors(errors: { [key: string]: string }): string[] {
        return Object.keys(errors).map((id) => {
            const matchingWs = selectedSchedules.find((ws) => ws.id === id);

            if (!matchingWs) {
                return `${id}: ${errors[id]}`;
            }
            return `${matchingWs.name}: ${errors[id]}`;
        });
    }

    function redirectToNewOtherSchedule(): void {
        navigate('new');
    }

    function resetTable(): void {
        setSelectedIds([]);
        setAction(undefined);
        tableRef?.current?.clear();
    }

    return (
        <>
            <DocumentBrowser locationSaveKey="browser-pos" />
            {action === 'EXCLUDE' && (
                <WorksheetExcludeConfirm
                    worksheetIds={canExclude.map((os) => os.id)}
                    onClose={(): void => {
                        setAction(undefined);
                    }}
                    onExclude={resetTable}
                />
            )}
            {action === 'INCLUDE' && (
                <WorksheetIncludeConfirm
                    worksheetIds={canInclude.map((os) => os.id)}
                    onClose={(): void => {
                        setAction(undefined);
                    }}
                    onInclude={resetTable}
                />
            )}

            {action === 'ADD_ASSIGNEES' && <AddAssigneesDialog worksheets={selectedSchedules} onClose={resetTable} />}
            {action === 'REMOVE_ASSIGNEES' && <RemoveAssigneesDialog worksheets={selectedSchedules} onClose={resetTable} />}
            {action === 'EDIT_DETAILS' && selectedSchedules[0] && <EditDetailsForm worksheet={selectedSchedules[0]} onClose={resetTable} />}
            {action === 'REVIEW' && (
                <WorksheetReviewConfirm
                    worksheetIds={filterCanReview(selectedSchedules).map((ws) => ws.id)}
                    onClose={(): void => setAction(undefined)}
                    onReview={resetTable}
                    onError={(e): void => setBulkActionErrors(formatErrors(e))}
                />
            )}
            {action === 'LINK_TO_PRIOR' && (
                <Modal isOpen onClose={(): void => setAction(undefined)}>
                    <div className="py-8 px-12 w-[1200px] h-[800px] flex flex-col">
                        <div className="overflow-auto grow px-1">
                            <LinkPriorWorksheetsContainer
                                priorWorkpaperId={priorWorkpaperId!}
                                selectedWorksheets={canLinkToPrior}
                                accountWorksheets={false}
                                onLink={resetTable}
                            />
                        </div>
                        <div className="border-t pt-4 mt-2 flex justify-end gap-2">
                            <ButtonV2 onClick={() => setAction(undefined)} label="Close" size="sm" type="primary" />
                        </div>
                    </div>
                </Modal>
            )}
            {action === 'CARRY_FORWARD' && (
                <BulkCarryForwardResourcesConfirm
                    selectedWorksheets={canCarryForward}
                    priorWorkpaperId={priorWorkpaperId!}
                    onClose={(): void => setAction(undefined)}
                    onCarryForward={resetTable}
                />
            )}
            <div className="pb-4">
                {uploading && (
                    <div className="absolute top-0 left-0 h-full w-full bg-white/60 z-50 flex items-center justify-center">
                        <Spinner />
                    </div>
                )}
                <div className="flex flex-col justify-between flex-wrap gap-2 mb-8">
                    <div className="mb-[-8px]">
                        <NavBreadcrumbs />
                    </div>
                    <section className="flex justify-between flex-wrap gap-y-6 mb-2">
                        <PageTitle title="Other Schedules" />
                        {selectedSchedules.length === 0 && <ButtonV2 size="sm" label="New Schedule" onClick={redirectToNewOtherSchedule} />}

                        {selectedSchedules.length > 0 && (
                            <SecondaryPageActions maxActionsCount={4}>
                                <SecondaryPageActions.Button
                                    label="Edit Details"
                                    onClick={(): void => setAction('EDIT_DETAILS')}
                                    disabled={readOnly || selectedSchedules.length !== 1}
                                />
                                <SecondaryPageActions.Button
                                    label="Add Assignees"
                                    onClick={(): void => setAction('ADD_ASSIGNEES')}
                                    disabled={readOnly}
                                />
                                <SecondaryPageActions.Button
                                    label="Remove Assignees"
                                    onClick={(): void => setAction('REMOVE_ASSIGNEES')}
                                    disabled={readOnly || selectedSchedules.every((os) => os.assigned.length === 0)}
                                />
                                <SecondaryPageActions.Button
                                    label={`Include (${canInclude.length})`}
                                    onClick={(): void => setAction('INCLUDE')}
                                    disabled={readOnly || canInclude.length === 0}
                                />
                                <SecondaryPageActions.Button
                                    label={`Exclude (${canExclude.length})`}
                                    onClick={(): void => setAction('EXCLUDE')}
                                    disabled={readOnly || canExclude.length === 0}
                                    hidden={false}
                                />

                                <SecondaryPageActions.Button
                                    label={`Link to Prior (${canLinkToPrior.length})`}
                                    onClick={(): void => setAction('LINK_TO_PRIOR')}
                                    disabled={readOnly || canLinkToPrior.length === 0}
                                    disabledHint={
                                        !priorWorkpaperId
                                            ? 'This workpaper is not linked to a prior workpaper'
                                            : canLinkToPrior.length === 0
                                            ? 'No worksheets eligible to be linked to a prior worksheet'
                                            : undefined
                                    }
                                    collapsible={true}
                                    staffOnly={true}
                                />
                                <SecondaryPageActions.Button
                                    label={`Carry Forward (${canCarryForward.length})`}
                                    onClick={(): void => setAction('CARRY_FORWARD')}
                                    disabled={readOnly || canCarryForward.length === 0}
                                    disabledHint={
                                        !priorWorkpaperId
                                            ? 'This workpaper is not linked to a prior workpaper'
                                            : canCarryForward.length === 0
                                            ? 'No selected worksheet has been linked to a prior worksheet'
                                            : undefined
                                    }
                                    collapsible={true}
                                    staffOnly={true}
                                />

                                <SecondaryPageActions.Button
                                    label={`Review (${filterCanReview(selectedSchedules).length})`}
                                    type="primary"
                                    onClick={(): void => setAction('REVIEW')}
                                    disabled={readOnly || !userCanReview || filterCanReview(selectedSchedules).length === 0}
                                    disabledHint={!userCanReview ? 'Only reviewers or approvers can review worksheets.' : undefined}
                                    collapsible={false}
                                />
                            </SecondaryPageActions>
                        )}
                    </section>
                    <section>
                        <OtherSchedulesTabs onChange={handleStatusTabChange} selectedTabIndex={statusIndex} />
                        <GrowHeightContainer className="flex flex-wrap items-center gap-2 mt-4">
                            <FilterV2
                                onChange={({ queryString }): void => {
                                    setFilter(queryString);
                                }}
                            >
                                {statusIndex === 0 && (
                                    <FilterV2.CheckboxFilter
                                        options={[
                                            { label: 'Unprepared', value: WorksheetStatusEnum.UNPREPARED },
                                            { label: 'In Progress', value: WorksheetStatusEnum.IN_PROGRESS },
                                            { label: 'Awaiting Review', value: WorksheetStatusEnum.AWAITING_REVIEW },
                                            { label: 'Reviewed', value: WorksheetStatusEnum.REVIEWED },
                                            { label: 'Excluded', value: WorksheetStatusEnum.EXCLUDED },
                                        ]}
                                        id="status"
                                        label="Status"
                                    />
                                )}
                                <FilterV2.ToggleFilter id="outstandingComments" label="Outstanding Comments" />
                                <FilterV2.ToggleFilter id="outstandingQueries" label="Outstanding Queries" />
                                <FilterV2.SelectFilter
                                    id="assigned"
                                    label="Assigned"
                                    values={[]}
                                    options={
                                        usersQuery.data?.users?.map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` })) ?? []
                                    }
                                />
                            </FilterV2>
                            <SearchV2 placeholder="Search schedules..." onChange={setSearch} />
                            <SortV2
                                options={statusIndex === 0 ? [{ label: 'Status', value: 'status' }, ...sortOptions] : sortOptions}
                                defaultValue="name"
                                defaultDirection="asc"
                                onChange={({ queryString }): void => {
                                    setSort(queryString);
                                }}
                            />
                        </GrowHeightContainer>
                    </section>
                </div>
                {/* Alert any bulk action errors */}
                {bulkActionErrors && (
                    <div className="mb-4">
                        <BulkActionsErrorAlert
                            errors={bulkActionErrors}
                            onClose={(): void => {
                                setBulkActionErrors(undefined);
                                resetTable();
                            }}
                        />
                    </div>
                )}
                <section className="mt-4">
                    <OtherSchedulesTable
                        onSelect={setSelectedIds}
                        onData={setCurrentSchedules}
                        tableRef={tableRef}
                        onUploading={setUploading}
                        filterQuery={filter}
                        searchQuery={search}
                        sortQuery={sort}
                        tab={statusIndex > 0 ? STATUS_FILTERS[statusIndex] : undefined}
                    />
                </section>
            </div>
        </>
    );
}
export default OtherSchedulesPage;
